package com.sweetlime.bingeamigo.service

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.functions.functions
import kotlinx.serialization.Serializable
import kotlin.math.pow

class XpService {

    @Serializable
    private data class AwardXpRequest(val action: String, val entityId: String)

    companion object {
        const val XP_WATCHLIST_ADD_MOVIE = 5
        const val XP_WATCHED_MOVIE = 20
        const val XP_FAVORITE_MOVIE = 10
        const val XP_RATED_MOVIE = 15
        const val XP_REVIEWED_MOVIE = 25
        const val XP_FRIEND_ADDED = 20
        const val XP_REACTION_RECEIVED = 2

        // TV show XP values
        const val XP_TRACKING_ADD_SHOW = 5
        const val XP_FAVORITE_SHOW = 10
        const val XP_SHOW_COMPLETED = 75
        const val XP_EPISODE_WATCHED = 3

        const val XP_RATED_SHOW = 15
        const val XP_REVIEWED_SHOW = 25

        // Action string constants matching CLAUDE.md spec
        const val ACTION_WATCHLIST_ADD_MOVIE = "watchlist_add_movie"
        const val ACTION_WATCHED_MOVIE = "watched_movie"
        const val ACTION_FAVORITE_MOVIE = "favorite_movie"
        const val ACTION_RATED_MOVIE = "rated_movie"
        const val ACTION_REVIEWED_MOVIE = "reviewed_movie"
        const val ACTION_FRIEND_ADDED = "friend_added"
        const val ACTION_REACTION_RECEIVED = "reaction_received"

        // TV show action constants
        const val ACTION_TRACKING_ADD_SHOW = "tracking_add_show"
        const val ACTION_FAVORITE_SHOW = "favorite_show"
        const val ACTION_SHOW_COMPLETED = "show_completed"
        const val ACTION_EPISODE_WATCHED = "episode_watched"
        const val ACTION_RATED_SHOW = "rated_show"
        const val ACTION_REVIEWED_SHOW = "reviewed_show"

        fun levelFromXp(xp: Int): Int {
            for (level in 100 downTo 2) {
                if (xp >= xpRequired(level)) return level
            }
            return 1
        }

        // xpRequired(level) = 1000 * (level - 1) ^ 1.65; level 1 = 0 XP
        fun xpRequired(level: Int): Int {
            if (level <= 1) return 0
            return (1000.0 * (level - 1).toDouble().pow(1.65)).toInt()
        }

        fun levelName(level: Int): String = when (level) {
            1   -> "Couch Potato"
            2   -> "Remote Holder"
            3   -> "First Episode"
            4   -> "Pilot Season"
            5   -> "Casual Viewer"
            6   -> "Weekend Watcher"
            7   -> "Snack Fetcher"
            8   -> "Subtitle Reader"
            9   -> "Skip Intro"
            10  -> "Binge Starter"
            11  -> "Two Episodes In"
            12  -> "Series Curious"
            13  -> "Watchlist Builder"
            14  -> "Late Night Watcher"
            15  -> "Episode Hoarder"
            16  -> "Autoplay Victim"
            17  -> "Next Episode Clicked"
            18  -> "Buffer Starer"
            19  -> "Plot Theorist"
            20  -> "Season One Done"
            21  -> "Committed Viewer"
            22  -> "Mid-Season Hooked"
            23  -> "Season Finisher"
            24  -> "Recap Skipper"
            25  -> "Cliffhanger Survivor"
            26  -> "Series Regular"
            27  -> "Rewatch Offender"
            28  -> "Spoiler Avoider"
            29  -> "Second Screen User"
            30  -> "Three Seasons Deep"
            31  -> "Show Recommender"
            32  -> "Character Namer"
            33  -> "Quote Dropper"
            34  -> "Ending Judger"
            35  -> "Genre Explorer"
            36  -> "Comfort Show Owner"
            37  -> "Rerun Champion"
            38  -> "Five Shows Juggled"
            39  -> "Binge Veteran"
            40  -> "Halfway There"
            41  -> "The Completionist"
            42  -> "Review Writer"
            43  -> "Rating Regular"
            44  -> "Taste Maker"
            45  -> "Friend Enabler"
            46  -> "The Persuader"
            47  -> "Ten Shows Strong"
            48  -> "Feed Dominator"
            49  -> "Streak Keeper"
            50  -> "Halfway Legend"
            51  -> "Fifty Levels In"
            52  -> "The Committed"
            53  -> "Nightly Ritual"
            54  -> "Weekend Marathon"
            55  -> "Show Finisher"
            56  -> "Series Collector"
            57  -> "The Bingemaster"
            58  -> "Streak Warrior"
            59  -> "Review Authority"
            60  -> "Friend of the Feed"
            61  -> "Sixty Levels Strong"
            62  -> "Catalog Diver"
            63  -> "Deep Cut Finder"
            64  -> "The Obscurist"
            65  -> "Quality Gatekeeper"
            66  -> "Taste Architect"
            67  -> "The Cinephile"
            68  -> "Weekend Devoted"
            69  -> "The Archivist"
            70  -> "Completionist Pro"
            71  -> "Seventy Levels Strong"
            72  -> "The Enthusiast"
            73  -> "Decade Diver"
            74  -> "Genre Sovereign"
            75  -> "The Marathon Runner"
            76  -> "Show Historian"
            77  -> "The Curator"
            78  -> "Feed Legend"
            79  -> "The Devoted"
            80  -> "Eighty Levels Strong"
            81  -> "The Obsessive"
            82  -> "Series Oracle"
            83  -> "The Forecaster"
            84  -> "Crowd Mover"
            85  -> "The Inner Circle"
            86  -> "Show Whisperer"
            87  -> "The Authority"
            88  -> "Top of the Feed"
            89  -> "The Untouchable"
            90  -> "Near the Summit"
            91  -> "Ninety Five Reached"
            92  -> "The Penultimate"
            93  -> "Almost Immortal"
            94  -> "Hall of Fame"
            95  -> "The Mythic"
            96  -> "The Amigo Legend"
            97  -> "The Undeniable"
            98  -> "The One and Only"
            99  -> "The Eternal Amigo"
            100 -> "The Amigo"
            else -> "Couch Potato"
        }
    }

    // XP is awarded by the `awardXp` Cloud Function: the recipient is always the
    // authenticated caller and the amount is decided server-side, so a modified
    // client cannot forge xp/level. `userId` and `xpAmount` are kept for call-site
    // readability but ignored by the backend. Idempotency and level recompute are
    // handled in the function. Errors are swallowed (XP is best-effort).
    suspend fun awardXp(userId: String, action: String, entityId: String, xpAmount: Int) {
        runCatching {
            Firebase.functions
                .httpsCallable("awardXp")
                .invoke(AwardXpRequest(action, entityId))
        }
    }
}